/* eslint-disable no-console */
import { Insights } from '@mui/icons-material';
import { PolicyValueType } from 'components/PolicyFilterDropdown';
import { atom, useAtom } from 'jotai';
import { atomWithReset, atomWithStorage, useResetAtom } from 'jotai/utils';
import { showInfoToast } from 'lib/ToastHelper';
import { companyIdAtom } from 'views/state';
import { DEFAULT_PAGE_SIZE } from 'views/types';

export const DEFAULT_COLUMN_KEYS_PC = [
  // 'id',
  'control_cid',
  'control_statement',
  'policy_title',
  'host_name',
  'control_criticality_label',
  'status',
  'evaluation_date',
  'age',
  'technology_name',
  'control_category',
  'assignee_name',
  'due_date',
  // 'instance',
  // 'remediation',
  // 'posture_modified_date',
  // 'first_fail_date',
  // 'last_fail_date',
  // 'first_pass_date',
  // 'last_pass_date',
  'actions',
];

const DateFilterKeys = [
  'modified_date',
  'evaluation_date',
  'first_fail_date',
  'last_fail_date',
  'first_pass_date',
  'last_pass_date',
];

export const columnKeysPCAtom = atomWithStorage(
  'pc_findings_listing_active_column_keys',
  DEFAULT_COLUMN_KEYS_PC,
);

export const filterValuesAtom = atomWithStorage<Record<string, any>>(
  'pc_findings_listing_filter_values_atom',
  {
    status: ['Passed', 'Failed'],
  },
);

export const parsedFilterValuesAtom = atom<Record<string, any>>((get) => {
  const filterValues = get(filterValuesAtom);
  const updatedFilterValues = {
    ...filterValues,
    ...DateFilterKeys.reduce((acc, key) => {
      if (filterValues[key]) {
        acc[key] = filterValues[key].map((dateRange) => ({
          from: dateRange.from ? new Date(dateRange.from) : null,
          to: dateRange.to ? new Date(dateRange.to) : null,
        }));
      }
      return acc;
    }, {}),
  };
  return updatedFilterValues;
});

export const selectedRowIdsAtom = atomWithReset<number[]>([]);
export const forceRerenderFindingGridAtom = atom<string>('');
export const findingGridDataRefreshAtom = atomWithStorage<string>(
  'pc_findings_refresh',
  '',
);

export const paginationConfigAtom = atomWithStorage(
  'pc_findings_pagination_config',
  {
    page_size: DEFAULT_PAGE_SIZE,
    page: 1,
  },
);
export const orderbyAtom = atomWithReset<string>('policy');
export const sortOrderAtom = atomWithReset<'desc' | 'asc'>('desc');

export const policyIdFilterAtom = atomWithStorage<PolicyValueType>(
  'pc_findings_policy_filter',
  'all',
);

export const isQuerystringFiltersInitializedAtom = atom<boolean>(false);

export const findingsDataQueryParamsAtom = atom(async (get) => {
  const companyId = get(companyIdAtom);
  const policyId = get(policyIdFilterAtom);
  const paginationConfig = get(paginationConfigAtom);
  const orderBy = get(orderbyAtom);
  const order = get(sortOrderAtom);
  // Add id to orderBy if not present, then apply sort order
  const orderByWithId = orderBy
    .split(',')
    .some((field) => field.trim() === 'id' || field.trim() === '-id')
    ? orderBy
    : `${orderBy},id`;
  const ordering = orderByWithId
    .split(',')
    .map(
      (field) =>
        `${order === 'desc' && !field.startsWith('-') ? '-' : ''}${field}`,
    )
    .join(',');
  const filterValues = get(parsedFilterValuesAtom);
  const _findingGridDataRefresh = get(findingGridDataRefreshAtom);

  if (companyId && companyId !== -1) {
    const params = {
      company_id: companyId.toString(),
      page: paginationConfig.page.toString(),
      page_size: paginationConfig.page_size.toString(),
      ordering: ordering,
    };

    // apply policy filter
    if (policyId !== 'all') {
      params['policy_id'] = policyId;
    }

    Object.entries(filterValues).forEach(([key, value]) => {
      if (value && value.length > 0) {
        if (key === 'predefined_filters') {
          value.forEach((element) => {
            // console.log(`in state: ${element}`);
            const [paramKey, paramValue] = element.split('=');
            params[paramKey] = paramValue;
          });
        } else if (DateFilterKeys.includes(key)) {
          const fromDate = value[0]?.from;
          const toDate = value[0]?.to;
          if (fromDate instanceof Date) {
            params[`${key}_after`] = fromDate.toISOString();
          } else {
            console.error({
              msg: 'fromDate is not a valid Date object:',
              fromDate,
            });
          }
          if (toDate) params[`${key}_before`] = toDate?.toISOString();
        } else if (key === 'host_ids') {
          // here value is an array of host information
          // so we need to extract the host ids
          const hostIds = value.map((host) => host.host_id);
          params[key] = hostIds.join(',');
        } else if (key === 'criticality') {
          params['control_criticality_value'] = value.join(',');
        } else if (key === 'age') {
          const minValue = value[0]?.minValue;
          const maxValue = value[0]?.maxValue;
          const isMinExclusive = value[0]?.isMinExclusive;
          const isMaxExclusive = value[0]?.isMaxExclusive;
          if (minValue) {
            const paramKey = isMinExclusive ? 'age_min_exclusive' : 'age_min';
            params[paramKey] = minValue;
          }
          if (maxValue) {
            const paramKey = isMaxExclusive ? 'age_max_exclusive' : 'age_max';
            params[paramKey] = maxValue;
          }
        } else {
          params[key] = value.join(',');
        }
      }
    });

    return params;
  }
  return undefined;
});

interface Insights {
  total: number;
  passed: number;
  failed: number;
  error: number;
  exception: number;
  urgent: number;
  critical: number;
  serious: number;
  medium: number;
  minimal: number;
  unique_assets: number;
  unique_controls: number;
  unique_technologies: number;
  unique_categories: number;
  unique_policies: number;
  watching_by_me_count: number;
  assigned_to_me_count: number;
  unique_unread_notes_count: number;
}

export const findingsDataAtom = atom({
  results: [],
  insights: {} as Insights,
  count: 0,
  loading: false,
});

export function useOnFilterChangeResetRelatedAtoms() {
  const resetSelectedRowIdsAtom = useResetAtom(selectedRowIdsAtom);
  const resetOrderbyAtom = useResetAtom(orderbyAtom);
  // const resetSortOrderAtom = useResetAtom(sortOrderAtom);
  const [paginationConfig, setPaginationConfig] = useAtom(paginationConfigAtom);

  const resetAtoms = () => {
    resetSelectedRowIdsAtom();
    resetOrderbyAtom();
    // resetSortOrderAtom();
    setPaginationConfig({
      ...paginationConfig,
      page: 1,
    });
  };

  return resetAtoms;
}

export const useResetAllPCFindingsAtoms = () => {
  const resetRelatedAtoms = useOnFilterChangeResetRelatedAtoms();
  const resetFilterValuesAtom = useResetAtom(filterValuesAtom);

  return () => {
    resetRelatedAtoms();
    resetFilterValuesAtom();
    // Reset other atoms if needed
  };
};

/**
 * Hook to reset relevant atoms when the policy filter changes, resetting:
 * - filterValuesAtom (only The Technology and Category filters)
 * - selectedRowIdsAtom
 * - orderbyAtom
 * - sortOrderAtom
 * - paginationConfigAtom
 * @returns a function that resets the selectedRowIdsAtom, orderbyAtom, sortOrderAtom, and paginationConfigAtom
 */
export const useOnPolicyFilterChangeResetRelatedAtoms = () => {
  const resetAtoms = useOnFilterChangeResetRelatedAtoms();
  const [filterValues, setFilterValues] = useAtom(filterValuesAtom);

  // 1. on company change reset all filters in filter menu, and policy filter, and related atoms, so, we need a separate hook for company change
  // 2. however, on policy filter change, we only want to drop certain filters in filter menu and add a toast message (alert about that)
  // 3. Also the PC dashboard hook for reset related atoms on company change is missing, and we need to add it
  return () => {
    resetAtoms();
    const resetFilterNames: string[] = [];

    if (filterValues.technology_id && filterValues.technology_id.length > 0) {
      resetFilterNames.push('Technology');
    }
    if (filterValues.category && filterValues.category.length > 0) {
      resetFilterNames.push('Category');
    }

    setFilterValues({
      ...filterValues,
      technology_id: [],
      category: [],
      // add more filters to reset if needed
    });

    if (resetFilterNames.length > 0) {
      showInfoToast(
        `${resetFilterNames.join(', ')} filter${
          resetFilterNames.length > 1 ? 's' : ''
        } ${
          resetFilterNames.length > 1 ? 'have' : 'has'
        } been reset due to policy filter change. Please reapply your filter selection${
          resetFilterNames.length > 1 ? 's' : ''
        } if needed.`,
      );
    }
  };
};

/**
 * reset the following atoms:
 * policy filter
 * all search and filter values
 * row selection
 * orderby
 * sort order
 * pagination
 */
export function useOnCompanyChangePCFindingsResetAtoms() {
  const resetPolicyIdAtom = useResetAtom(policyIdFilterAtom);
  const resetFilterValuesAtom = useResetAtom(filterValuesAtom);
  const resetRelatedAtoms = useOnFilterChangeResetRelatedAtoms();

  const resetAtoms = () => {
    resetPolicyIdAtom();
    resetFilterValuesAtom();
    resetRelatedAtoms();
  };

  return resetAtoms;
}
